import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  Review,
  ReviewListParams,
  PaginatedResponse,
  ModerateReviewRequest,
  BulkModerateRequest,
  BulkModerateResponse,
  ReviewStatus,
} from '@/types';

const REVIEWS_KEY = 'reviews';

export function useReviews(params: ReviewListParams) {
  return useQuery<PaginatedResponse<Review>>({
    queryKey: [REVIEWS_KEY, params],
    queryFn: () => apiClient.getReviews(params),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useReview(reviewId: string | null) {
  return useQuery<Review>({
    queryKey: [REVIEWS_KEY, reviewId],
    queryFn: () => apiClient.getReview(reviewId!),
    enabled: !!reviewId,
  });
}

export function useModerateReview() {
  const queryClient = useQueryClient();

  // The 4th generic is the onMutate context. Without it TypeScript infers `{}`
  // and `context.previousQueries` in onError is a type error — the optimistic
  // rollback would silently stop compiling if the shape changed.
  type ModerateContext = {
    previousQueries: ReturnType<
      typeof queryClient.getQueriesData<PaginatedResponse<Review>>
    >;
  };

  return useMutation<Review, Error, ModerateReviewRequest, ModerateContext>({
    mutationFn: (request) => apiClient.moderateReview(request),
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey: [REVIEWS_KEY] });

      const previousQueries = queryClient.getQueriesData<PaginatedResponse<Review>>({
        queryKey: [REVIEWS_KEY],
      });

      queryClient.setQueriesData<PaginatedResponse<Review>>(
        { queryKey: [REVIEWS_KEY] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((review) =>
              review.reviewId === request.reviewId
                ? {
                    ...review,
                    status:
                      request.action === 'approve'
                        ? ReviewStatus.Approved
                        : request.action === 'unapprove'
                        ? ReviewStatus.Pending
                        : ReviewStatus.Rejected,
                  }
                : review
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [REVIEWS_KEY] });
    },
  });
}

export function useDeleteReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reviewId: string) => apiClient.deleteReview(reviewId),
    onMutate: async (reviewId: string) => {
      await queryClient.cancelQueries({ queryKey: [REVIEWS_KEY] });

      const previousQueries = queryClient.getQueriesData<PaginatedResponse<Review>>({
        queryKey: [REVIEWS_KEY],
      });

      queryClient.setQueriesData<PaginatedResponse<Review>>(
        { queryKey: [REVIEWS_KEY] },
        (old) => {
          if (!old) return old;
          const items = old.items.filter((review) => review.reviewId !== reviewId);
          return {
            ...old,
            items,
            total: old.total - 1,
            totalPages: Math.ceil((old.total - 1) / old.pageSize),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err: Error, _vars: string, context?: { previousQueries: [unknown, PaginatedResponse<Review> | undefined][] }) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key as string[], data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [REVIEWS_KEY] });
    },
  });
}

export function useBulkModerate() {
  const queryClient = useQueryClient();

  return useMutation<BulkModerateResponse, Error, BulkModerateRequest>({
    mutationFn: (request) => apiClient.bulkModerateReviews(request),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [REVIEWS_KEY] });
    },
  });
}
